import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import './Legal.css';
import AppHeader from '../../components/AppHeader';
import Footer from '../../components/Footer/Footer';

function CookiePolicy() {
  const navigate = useNavigate();
  const lastUpdated = 'December 1, 2025';

  return (
    <div className="legal-page">
      <AppHeader showActions={false} />

      <div className="page-back-nav">
        <button
          className="back-to-home-btn"
          onClick={() => navigate('/loyalty-directory')}
          aria-label="Back to Home"
        >
          <FaArrowLeft />
          <span>Back to Home</span>
        </button>
      </div>

      <div className="legal-container">
        <header className="legal-header">
          <h1>Cookie Policy</h1>
          <p className="legal-last-updated">Last Updated: {lastUpdated}</p>
        </header>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              This Cookie Policy explains how Block Club ("we," "our," or "us") uses cookies and similar tracking technologies when you visit our website and use our loyalty program services (collectively, the "Services").
            </p>
            <p>
              This policy provides information about what cookies are, which cookies we use, how we use them, and how you can control them. By using our Services, you consent to our use of cookies in accordance with this Cookie Policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. What Are Cookies?</h2>
            <p>
              Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
            </p>
            <p>
              Cookies can be "persistent" or "session" cookies:
            </p>
            <ul>
              <li><strong>Session Cookies:</strong> Temporary cookies that expire when you close your browser</li>
              <li><strong>Persistent Cookies:</strong> Cookies that remain on your device until they expire or you delete them</li>
            </ul>
            <p>
              Cookies can also be first-party or third-party cookies:
            </p>
            <ul>
              <li><strong>First-Party Cookies:</strong> Set by the website you are visiting</li>
              <li><strong>Third-Party Cookies:</strong> Set by a domain other than the website you are visiting</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Cookies</h2>
            <p>
              We use cookies for the following purposes:
            </p>

            <h3>3.1 Essential Cookies</h3>
            <p>
              These cookies are necessary for the Services to function properly and cannot be disabled. They enable core functionality such as:
            </p>
            <ul>
              <li>User authentication and account access</li>
              <li>Security and fraud prevention</li>
              <li>Session management</li>
              <li>Load balancing</li>
              <li>Remembering your privacy preferences</li>
            </ul>
            <p><strong>Examples:</strong></p>
            <ul className="cookie-list">
              <li><code>loyalty_email</code> - Stores your email for authentication</li>
              <li><code>loyalty_email_verified</code> - Tracks email verification status</li>
              <li><code>loyalty_auth_token</code> - Maintains your login session</li>
              <li><code>company_signup_code</code> - Stores organization affiliation code</li>
            </ul>

            <h3>3.2 Functional Cookies</h3>
            <p>
              These cookies allow the Services to provide enhanced functionality and personalization:
            </p>
            <ul>
              <li>Remembering your preferences and settings</li>
              <li>Personalizing content and offers</li>
              <li>Storing venue cache data for faster loading</li>
              <li>Tracking wishlist items</li>
              <li>Maintaining user interface preferences</li>
            </ul>
            <p><strong>Examples:</strong></p>
            <ul className="cookie-list">
              <li><code>venue_cache_*</code> - Caches venue information for performance</li>
              <li><code>pending_venue_id</code> - Remembers pending venue actions</li>
              <li><code>redirect_to_venue</code> - Handles navigation after login</li>
            </ul>

            <h3>3.3 Analytics Cookies</h3>
            <p>
              These cookies help us understand how visitors interact with the Services by collecting and reporting information anonymously:
            </p>
            <ul>
              <li>Number of visitors and page views</li>
              <li>How users navigate the Services</li>
              <li>Which features are most popular</li>
              <li>Performance monitoring</li>
              <li>Error tracking and diagnostics</li>
            </ul>

            <h3>3.4 Targeting and Advertising Cookies</h3>
            <p>
              We currently do not use advertising cookies. If we implement them in the future, we will update this policy and seek your consent where required by law.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Cookies We Use</h2>
            <p>
              Below is a detailed list of the cookies we use on our Services:
            </p>

            <table className="cookie-table">
              <thead>
                <tr>
                  <th>Cookie Name</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>loyalty_email</code></td>
                  <td>Essential</td>
                  <td>30 days</td>
                  <td>Stores user email for authentication</td>
                </tr>
                <tr>
                  <td><code>loyalty_email_verified</code></td>
                  <td>Essential</td>
                  <td>30 days</td>
                  <td>Tracks email verification timestamp</td>
                </tr>
                <tr>
                  <td><code>loyalty_auth_token</code></td>
                  <td>Essential</td>
                  <td>Session</td>
                  <td>Maintains authenticated session</td>
                </tr>
                <tr>
                  <td><code>loyalty_customer_id</code></td>
                  <td>Essential</td>
                  <td>Session</td>
                  <td>Identifies customer account</td>
                </tr>
                <tr>
                  <td><code>company_signup_code</code></td>
                  <td>Functional</td>
                  <td>Session</td>
                  <td>Stores organization affiliation</td>
                </tr>
                <tr>
                  <td><code>venue_cache_*</code></td>
                  <td>Functional</td>
                  <td>Session</td>
                  <td>Caches venue data for performance</td>
                </tr>
                <tr>
                  <td><code>pending_venue_id</code></td>
                  <td>Functional</td>
                  <td>Session</td>
                  <td>Remembers pending venue actions</td>
                </tr>
                <tr>
                  <td><code>redirect_to_venue</code></td>
                  <td>Functional</td>
                  <td>Session</td>
                  <td>Manages post-login navigation</td>
                </tr>
                <tr>
                  <td><code>last_activity</code></td>
                  <td>Essential</td>
                  <td>30 minutes</td>
                  <td>Tracks session activity for security</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="legal-section">
            <h2>5. Local Storage and Session Storage</h2>
            <p>
              In addition to cookies, we use browser storage mechanisms (localStorage and sessionStorage) to store information locally on your device. These technologies work similarly to cookies but have different characteristics:
            </p>

            <h3>5.1 Local Storage</h3>
            <p>
              Data stored in localStorage persists until explicitly deleted. We use localStorage for:
            </p>
            <ul>
              <li>Long-term authentication persistence</li>
              <li>User preference settings</li>
              <li>Activity tracking for session timeout</li>
            </ul>

            <h3>5.2 Session Storage</h3>
            <p>
              Data stored in sessionStorage is cleared when the browser tab is closed. We use sessionStorage for:
            </p>
            <ul>
              <li>Temporary authentication data</li>
              <li>Navigation state</li>
              <li>Cached venue information</li>
              <li>Form data during active sessions</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Cookies</h2>
            <p>
              We may allow third-party service providers to place cookies on your device for the following purposes:
            </p>
            <ul>
              <li>Analytics and performance monitoring</li>
              <li>Error tracking and diagnostics</li>
              <li>Content delivery networks (CDNs)</li>
            </ul>
            <p>
              These third parties have their own privacy policies and cookie policies. We recommend reviewing their policies:
            </p>
            <ul className="third-party-links">
              <li>Analytics providers (if applicable)</li>
              <li>Cloud service providers</li>
              <li>CDN providers</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. How to Control Cookies</h2>
            <p>
              You have the right to accept or reject cookies. You can control cookies through:
            </p>

            <h3>7.1 Browser Settings</h3>
            <p>
              Most web browsers automatically accept cookies, but you can modify your browser settings to decline cookies if you prefer. Here's how to manage cookies in popular browsers:
            </p>
            <ul className="browser-links">
              <li><strong>Google Chrome:</strong> Settings &gt; Privacy and Security &gt; Cookies and other site data</li>
              <li><strong>Mozilla Firefox:</strong> Settings &gt; Privacy & Security &gt; Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies and website data</li>
              <li><strong>Microsoft Edge:</strong> Settings &gt; Cookies and site permissions &gt; Cookies and site data</li>
            </ul>
            <p>
              Please note that if you disable cookies, some features of the Services may not function properly.
            </p>

            <h3>7.2 Mobile Device Settings</h3>
            <p>
              Mobile devices also offer options to control tracking and cookies:
            </p>
            <ul>
              <li><strong>iOS:</strong> Settings &gt; Safari &gt; Privacy & Security</li>
              <li><strong>Android:</strong> Settings &gt; Google &gt; Ads &gt; Opt out of Ads Personalization</li>
            </ul>

            <h3>7.3 Do Not Track</h3>
            <p>
              Some browsers have a "Do Not Track" (DNT) feature that signals to websites that you do not want to be tracked. Currently, there is no industry standard for how to respond to DNT signals. We do not currently respond to DNT signals.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Cookie Retention</h2>
            <p>
              The retention period for cookies depends on their type:
            </p>
            <ul>
              <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
              <li><strong>Authentication Cookies:</strong> Typically expire after 30 days of inactivity</li>
              <li><strong>Preference Cookies:</strong> May persist for up to 1 year</li>
              <li><strong>Analytics Cookies:</strong> Generally expire after 2 years</li>
            </ul>
            <p>
              You can delete all cookies from your browser at any time through your browser settings.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Cookies and Personal Data</h2>
            <p>
              Some cookies may collect personal information, such as your email address or authentication tokens. This information is processed in accordance with our Privacy Policy. We implement appropriate security measures to protect cookie data from unauthorized access or disclosure.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. International Data Transfers</h2>
            <p>
              Information collected through cookies may be transferred to and stored in countries outside your country of residence. These countries may have different data protection laws. By using our Services, you consent to the transfer of information to these countries.
            </p>
            <p>
              For users in the European Economic Area (EEA), we ensure appropriate safeguards are in place for data transfers, such as Standard Contractual Clauses approved by the European Commission.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Updates to This Cookie Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in our cookie practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on this page and updating the "Last Updated" date.
            </p>
            <p>
              We encourage you to review this Cookie Policy periodically to stay informed about our use of cookies.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Your Consent</h2>
            <p>
              By continuing to use our Services, you consent to our use of cookies as described in this Cookie Policy. If you do not agree with our use of cookies, you should adjust your browser settings accordingly or refrain from using our Services.
            </p>
            <p>
              For essential cookies that are necessary for the Services to function, your consent is not required as they are necessary for the performance of the service you have requested.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. More Information</h2>
            <p>
              For more information about how we protect your privacy, please review our Privacy Policy. If you have questions about our use of cookies or other tracking technologies, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> support@blockclub.com</p>
              <p><strong>Address:</strong> Block Club, St. Louis, MO</p>
              <p><strong>Privacy Officer:</strong> privacy@blockclub.com</p>
            </div>
          </section>

          <section className="legal-section">
            <h2>14. Useful Resources</h2>
            <p>
              To learn more about cookies and how to manage them, visit these resources:
            </p>
            <ul className="resource-links">
              <li><a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">All About Cookies</a> - Comprehensive guide to cookies</li>
              <li><a href="https://www.youronlinechoices.com" target="_blank" rel="noopener noreferrer">Your Online Choices</a> - EU cookie information and opt-out</li>
              <li><a href="https://optout.networkadvertising.org" target="_blank" rel="noopener noreferrer">Network Advertising Initiative</a> - Opt-out of interest-based advertising</li>
            </ul>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default CookiePolicy;
